import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Hammer } from "lucide-react";
import { startGoogleSignIn } from "@/lib/oauth";

export default function AuthPage() {
  const [params] = useSearchParams();
  const initialTab = params.get("mode") === "signup" ? "signup" : "login";
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ name: "", email: "", password: "" });

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(signupForm.name, signupForm.email, signupForm.password);
      toast.success("Account created. Let's build your first quote.");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="px-5 sm:px-8 py-5">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="auth-logo-link">
          <div className="h-8 w-8 rounded-lg bg-orange-500 flex items-center justify-center text-white">
            <Hammer className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-bold text-slate-900">ScopeWriter <span className="text-orange-500">AI</span></span>
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-10">
        <Card className="w-full max-w-md p-8 shadow-card border border-slate-200">
          <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">
            {tab === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            {tab === "login" ? "Log in to manage your quotes." : "Start turning rough notes into polished quotes."}
          </p>

          {/* Social sign-in. REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH. */}
          <div className="space-y-2 mb-5">
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 font-medium"
              onClick={startGoogleSignIn}
              data-testid="google-signin-button"
            >
              <GoogleGlyph className="h-4 w-4 mr-2" />
              Continue with Google
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 font-medium opacity-70 cursor-not-allowed"
              disabled
              data-testid="apple-signin-button"
              title="Coming soon"
            >
              <AppleGlyph className="h-4 w-4 mr-2" />
              Continue with Apple
              <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">Coming soon</span>
            </Button>
          </div>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center"><span className="px-3 bg-white text-xs text-slate-400">or with email</span></div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Log in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Sign up</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={onLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email" type="email" required
                    data-testid="login-email-input"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password" type="password" required
                    data-testid="login-password-input"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  />
                </div>
                <Button disabled={loading} type="submit" data-testid="login-submit-button" className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                  {loading ? "Logging in…" : "Log in"}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="signup">
              <form onSubmit={onSignup} className="space-y-4">
                <div>
                  <Label htmlFor="signup-name">Your name</Label>
                  <Input
                    id="signup-name" required
                    data-testid="signup-name-input"
                    value={signupForm.name}
                    onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email" type="email" required
                    data-testid="signup-email-input"
                    value={signupForm.email}
                    onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password" type="password" required minLength={6}
                    data-testid="signup-password-input"
                    value={signupForm.password}
                    onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                  />
                </div>
                <Button disabled={loading} type="submit" data-testid="signup-submit-button" className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                  {loading ? "Creating account…" : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}

function GoogleGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.85h5.36c-.23 1.4-1.65 4.1-5.36 4.1-3.22 0-5.85-2.66-5.85-5.95 0-3.28 2.63-5.94 5.85-5.94 1.83 0 3.06.78 3.77 1.45l2.57-2.48C16.74 3.85 14.6 3 12 3 6.98 3 2.92 7.04 2.92 12.07c0 5.03 4.06 9.07 9.08 9.07 5.24 0 8.71-3.68 8.71-8.88 0-.6-.06-1.06-.14-1.51H12z"/>
    </svg>
  );
}

function AppleGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M16.37 12.79c.03 2.79 2.45 3.72 2.48 3.74-.02.07-.39 1.33-1.27 2.63-.77 1.13-1.57 2.25-2.83 2.27-1.24.03-1.64-.74-3.05-.74-1.42 0-1.86.71-3.04.77-1.22.05-2.15-1.22-2.92-2.35-1.58-2.3-2.79-6.5-1.16-9.33.81-1.41 2.25-2.3 3.82-2.33 1.2-.02 2.33.81 3.07.81.74 0 2.11-1 3.55-.86.6.03 2.3.24 3.39 1.83-.09.06-2.03 1.18-2.01 3.52zM14.36 5.42c.65-.79 1.09-1.88.97-2.97-.94.04-2.07.63-2.74 1.42-.6.7-1.13 1.81-.99 2.88 1.05.08 2.11-.53 2.76-1.33z"/>
    </svg>
  );
}

